import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useHistory, useLocation, useParams } from "react-router-dom";

import { Button, Flex, Icon, Text } from "@chakra-ui/react";
import { FaArrowLeft, FaUser, FaHome } from "react-icons/fa";

import AuditoriaDetalheHeader from "../../../components/AuditoriaDetalheHeader";
import FiltrosAplicadosChips from "../../../components/FiltrosAplicadosChips";
import TableAuditoria from "../../../components/TableAuditoria";
import AcaoAuditoriaBadge from "../../../components/AcaoAuditoriaBadge";
import AuditoriaService, {
  achatarEventos,
  formatarValorAuditoria,
  urlLogoServico,
} from "../../../../main/services/AuditoriaService";
import { useAuthProvider } from "../../../../Context/AuthProvider";

const CelulaValor = (props) => {
  const valor = formatarValorAuditoria(props.value);
  const referencia = props.row.original.referenciaRegistro;
  if (!referencia) return <Text>{valor || "-"}</Text>;
  return (
    <Flex flexDir="column">
      <Text fontWeight={700} fontStyle="italic">
        {referencia}
      </Text>
      <Text>{valor || "-"}</Text>
    </Flex>
  );
};

const columns = [
  {
    Header: "Data/Hora",
    accessor: "dataHora",
    Cell: (props) => <Text whiteSpace="nowrap">{props.value}</Text>,
  },
  {
    Header: "Usuário",
    accessor: "usuario",
    Cell: (props) => (
      <Flex flexDir="column">
        <Text>{props.value}</Text>
        <Text fontSize="12px" color="#6B6C71">
          {props.row.original.login}
        </Text>
        <Flex alignItems="center" gap="4px" fontSize="12px" color="#6B6C71">
          <Icon as={FaUser} boxSize="10px" />
          <Text fontWeight={700} fontStyle="italic">
            {props.row.original.perfil}
          </Text>
        </Flex>
        {props.row.original.orgao && (
          <Flex alignItems="center" gap="4px" fontSize="12px" color="#6B6C71">
            <Icon as={FaHome} boxSize="10px" />
            <Text fontStyle="italic">{props.row.original.orgao}</Text>
          </Flex>
        )}
      </Flex>
    ),
  },
  {
    Header: "Ação",
    accessor: "acao",
    Cell: (props) => <AcaoAuditoriaBadge acao={props.value} />,
  },
  { Header: "Aba", accessor: "aba" },
  {
    Header: "Campo",
    accessor: "campo",
    Cell: (props) => (
      <Text>{props.row.original.campoExibicao || props.value}</Text>
    ),
  },
  {
    Header: "Valor Anterior",
    accessor: "valorAnterior",
    Cell: CelulaValor,
  },
  {
    Header: "Valor Novo",
    accessor: "valorNovo",
    Cell: CelulaValor,
  },
];

const STORAGE_KEY = "auditoria-servico-detalhe";

const lerStorage = () =>
  JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");

const persistir = (overrides) => {
  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ ...lerStorage(), ...overrides }),
  );
};

const AuditoriaServico = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { selectedProfile } = useAuthProvider();

  const perfilPronto = !!selectedProfile;
  const chips = location.state?.chips || [];

  const filtrosOrigem = useMemo(
    () => location.state?.filters || {},
    [location.state],
  );

  const [detalhe, setDetalhe] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(() => lerStorage().pageSize || 10);
  const [sortField, setSortField] = useState(() => lerStorage().sortField);
  const [sortType, setSortType] = useState(() => lerStorage().sortType);
  const [loading, setLoading] = useState(true);

  const buscar = useCallback(
    (pagina, tamanhoPagina, campo, tipo) => {
      setLoading(true);
      AuditoriaService.buscarDetalheServico(
        id,
        filtrosOrigem,
        pagina,
        tamanhoPagina,
        campo,
        tipo,
      )
        .then((res) => {
          setDetalhe(res.data);
          document.title = `MCA - Auditoria - ${res.data.nome}`;
        })
        .finally(() => setLoading(false));
    },
    [id, filtrosOrigem],
  );

  useEffect(() => {
    if (!perfilPronto) return;

    const salvo = lerStorage();
    buscar(0, salvo.pageSize || 10, salvo.sortField, salvo.sortType);
    setPageIndex(0);
  }, [id, buscar, perfilPronto]);

  const handlePageChange = (novaPagina) => {
    setPageIndex(novaPagina);
    buscar(novaPagina, pageSize, sortField, sortType);
  };

  const handlePageSizeChange = (novoPageSize) => {
    setPageSize(novoPageSize);
    setPageIndex(0);
    persistir({ pageSize: novoPageSize });
    buscar(0, novoPageSize, sortField, sortType);
  };

  const handleSort = (campo) => {
    const novoTipo = sortField === campo && sortType === "ASC" ? "DESC" : "ASC";
    setSortField(campo);
    setSortType(novoTipo);
    setPageIndex(0);
    persistir({ sortField: campo, sortType: novoTipo });
    buscar(0, pageSize, campo, novoTipo);
  };

  const handleVoltar = () => history.push("/auditoria");

  const linhas = detalhe ? achatarEventos(detalhe.eventos) : [];
  const totalRegistros = detalhe?.totalCampos ?? 0;

  return (
    <Flex
      flexDir="column"
      w="100%"
      px={["5px", "32px"]}
      pb="20px"
      pt="4px"
      gap="18px"
    >
      {detalhe && (
        <AuditoriaDetalheHeader
          imagem={detalhe.imagem ? urlLogoServico(id) : null}
          titulo={detalhe.nomeFantasia || detalhe.nome}
          subtitulos={[
            detalhe.nomeSocial && `Nome Social: ${detalhe.nomeSocial}`,
            detalhe.promotoria,
          ]}
          visao="SERVICO"
          id={id}
          filtrosOrigem={filtrosOrigem}
        />
      )}

      <Flex
        w="100%"
        backgroundColor="#FFF"
        borderRadius="10px"
        p="18px 24px"
        flexDir="row"
        flexWrap="wrap"
        alignItems="center"
        gap="10px"
      >
        <Text
          fontWeight={700}
          fontSize="14px"
          color="#169499"
          whiteSpace="nowrap"
        >
          FILTROS APLICADOS:
        </Text>
        <FiltrosAplicadosChips chips={chips} />
      </Flex>

      <TableAuditoria
        columns={columns}
        data={linhas}
        loading={loading}
        entidade="auditoria-servico-detalhe"
        pageIndex={detalhe?.pagina ?? pageIndex}
        pageSize={pageSize}
        pageCount={detalhe?.totalPaginas ?? 0}
        totalItems={totalRegistros}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        sortField={sortField}
        sortType={sortType}
        onSort={handleSort}
      />

      <Flex justifyContent="flex-end" gap={4}>
        <Button
          leftIcon={<FaArrowLeft />}
          color="#FFF"
          backgroundColor="#169499"
          fontWeight={700}
          fontSize="15px"
          _hover={{ bg: "#169499" }}
          px="35px"
          onClick={handleVoltar}
        >
          Voltar
        </Button>
      </Flex>
    </Flex>
  );
};

export default AuditoriaServico;
